import { useTranslation } from 'react-i18next';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import HintBlock from '../../../components/common/HintBlock';
import { LouiseBadgeIcon } from '../../../components/common/icons/louiseBadge.icon';
import { PARTNER_LEVEL } from '../../../utils/partnerObject';

interface Props {
  sales: number;
  currentTarget: number;
  level: PARTNER_LEVEL.STANDARD | PARTNER_LEVEL.BRONZE | PARTNER_LEVEL.SILVER | PARTNER_LEVEL.GOLD;
  nextLevel: PARTNER_LEVEL.STANDARD | PARTNER_LEVEL.BRONZE | PARTNER_LEVEL.SILVER | PARTNER_LEVEL.GOLD;
}

function PartnerAffiliateLevel({ sales, level, nextLevel, currentTarget }: Props) {
  const { t } = useTranslation();
  const progressPercentage = currentTarget > 0 ? Math.min((sales / currentTarget) * 100, 100) : 100;

  return (
    <div className="tw-w-full tw-px-6 tw-py-5 tw-bg-white tw-rounded-2xl tw-shadow tw-grid tw-gap-6">
      <div className="tw-grid tw-grid-cols-[auto_1fr] tw-gap-4 tw-items-center">
        <div className="tw-w-11 tw-h-11 tw-relative">
          <LouiseBadgeIcon theme={level} width="48px" height="48px" />
        </div>
        <div className="tw-flex tw-flex-col">
          <div className="tw-text-gray-800 tw-text-base tw-font-medium tw-font-['Lexend']">
            {t('BusinessPartner/LevelCounter/actualLevel')}
            {level ?? 'Standard'}
          </div>
          {!level && <HintBlock texts={[t('BusinessPartner/LevelCounter/unlockYourPayout')]} />}
        </div>
      </div>

      <div className="tw-w-full tw-flex tw-flex-col tw-gap-2">
        <div className="tw-relative tw-h-2 tw-bg-gray-100 tw-rounded-full">
          <div className="tw-absolute tw-h-2 tw-bg-primary tw-rounded-full" style={{ width: `${progressPercentage}%` }} />
        </div>
        <div className="tw-grid tw-grid-cols-2 tw-items-center">
          <div className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend']">
            {sales} / {currentTarget > 0 ? currentTarget : '∞'}
          </div>
          {nextLevel && currentTarget > 0 && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend']">{t('BusinessPartner/LevelCounter/nextLevel')}</div>
              <div className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-flex tw-items-center">
                {nextLevel}
                <span className="tw-ml-2">
                  <LouiseBadgeIcon theme={nextLevel} width="25px" height="25px" />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WrappedErrorBoundary(PartnerAffiliateLevel);
